import React, { useEffect, useRef, useState } from "react";
import { compressLogo } from "@/lib/compressLogo";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Palette, Check, RotateCcw, Save, Sun, Moon, Laptop, Upload, X as XIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import {
  ACCENT_PRESETS, applyAccent, getAccent, setAccent, resetAccent,
  getTheme, setTheme,
} from "@/lib/theme";

/**
 * Settings → Appearance · Iter-193
 * Lets tenant admins pick:
 *   • Base theme  (Light / System / Dark) — same picker as topbar
 *   • Accent colour — 9 curated presets + custom hex picker
 *
 * The accent choice is persisted in `Organization.appearance` (via
 * `PATCH /api/settings/appearance`) so every user of the tenant sees
 * the same brand palette. It is also cached in localStorage for
 * instant first-paint on subsequent visits.
 */
export default function AppearanceSettings() {
  const { org, user, patchOrgLocal } = useAuth();
  const canEdit = ["business_owner", "super_admin", "finance_manager"].includes(user?.role);
  const orgAccent = (org?.appearance?.accent_hex) || getAccent()?.hex || ACCENT_PRESETS[0].hex;
  const [hex, setHex] = useState(orgAccent);
  const [themeMode, setThemeMode] = useState(() => getTheme());
  const [busy, setBusy] = useState(false);

  // Live preview while user is choosing — reverts on unmount if not saved
  useEffect(() => { applyAccent(hex); }, [hex]);

  const pickPreset = (p) => setHex(p.hex);
  const pickTheme = (mode) => { setTheme(mode); setThemeMode(mode); };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/settings/appearance", { accent_hex: hex });
      setAccent(hex, "org");
      patchOrgLocal({ appearance: { ...(org?.appearance || {}), accent_hex: hex } });
      toast.success("Appearance saved — every user of this company will see the new palette");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const reset = () => {
    resetAccent();
    setHex(ACCENT_PRESETS[0].hex);
  };

  const isValidHex = /^#[0-9a-f]{6}$/i.test(hex);
  const themeIcons = { light: Sun, system: Laptop, dark: Moon };

  return (
    <div className="p-6 space-y-6 max-w-4xl" data-testid="appearance-settings">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// Personalisation</div>
        <h1 className="text-3xl font-heading font-bold mt-1">Appearance</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Pick a theme + brand colour that applies to every user of <b>{org?.name}</b>. Changes are live for you immediately; save to push to everyone.
        </p>
      </div>

      {/* Theme mode */}
      <Card className="p-5 rounded-sm" data-testid="theme-mode-card">
        <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 mb-3">Base theme</div>
        <div className="grid grid-cols-3 gap-3">
          {["light", "system", "dark"].map(m => {
            const Icon = themeIcons[m];
            const on = themeMode === m;
            return (
              <button key={m} onClick={() => pickTheme(m)} data-testid={`appearance-theme-${m}`}
                className={`rounded-sm border p-4 text-left transition-colors ${on ? "border-accent bg-accent/5 ring-1 ring-accent/40" : "border-border hover:border-accent/40"}`}>
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4" />
                  <div className="text-sm font-medium capitalize">{m}</div>
                  {on && <Check className="w-4 h-4 ml-auto text-accent" />}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  {m === "light" && "Bright canvas · high contrast text"}
                  {m === "system" && "Follows your operating system setting"}
                  {m === "dark" && "Low-glare dark canvas · night-friendly"}
                </div>
              </button>
            );
          })}
        </div>
      </Card>

      {/* Accent colour */}
      <Card className="p-5 rounded-sm" data-testid="accent-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Brand accent</div>
            <div className="text-xs text-muted-foreground mt-0.5">Applies to buttons, links, focus rings, badges and highlights.</div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-mono text-muted-foreground">{hex.toUpperCase()}</span>
            <div className="w-7 h-7 rounded-sm border border-border" style={{ backgroundColor: hex }} data-testid="accent-swatch" />
          </div>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-5 gap-2.5">
          {ACCENT_PRESETS.map(p => {
            const on = hex.toLowerCase() === p.hex.toLowerCase();
            return (
              <button key={p.key} onClick={() => pickPreset(p)} data-testid={`accent-${p.key}`}
                className={`group rounded-sm border p-3 text-left transition-all ${on ? "border-accent ring-1 ring-accent/40" : "border-border hover:border-accent/40"}`}>
                <div className="w-full h-8 rounded-sm mb-2 shadow-sm" style={{ backgroundColor: p.hex }} />
                <div className="text-[12px] font-medium">{p.name}</div>
                <div className="text-[10px] font-mono text-muted-foreground mt-0.5">{p.hex}</div>
              </button>
            );
          })}
        </div>
        <div className="mt-4 pt-4 border-t border-border">
          <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 mb-1.5">Custom hex</div>
          <div className="flex items-center gap-2 max-w-sm">
            <input type="color" value={isValidHex ? hex : "#F97316"}
              onChange={(e) => setHex(e.target.value)}
              className="h-9 w-12 rounded-sm cursor-pointer border border-border"
              data-testid="accent-color-picker" />
            <Input value={hex} onChange={(e) => setHex(e.target.value)}
              placeholder="#F97316" className="h-9 rounded-sm font-mono text-sm"
              data-testid="accent-hex-input" />
          </div>
          {!isValidHex && <div className="text-[11px] text-destructive mt-1">Enter a valid 6-digit hex (e.g. #2563EB)</div>}
        </div>
      </Card>

      {/* Live preview */}
      <Card className="p-5 rounded-sm" data-testid="preview-card">
        <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 mb-3">Live preview</div>
        <div className="flex flex-wrap items-center gap-3">
          <Button data-testid="preview-btn-primary">Primary button</Button>
          <Button variant="outline" className="border-accent text-accent">Outline accent</Button>
          <a href="#" className="text-accent hover:underline text-sm">Text link</a>
          <span className="inline-block px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-widest bg-accent/15 text-accent">badge</span>
          <input placeholder="Input ring on focus" className="h-9 px-3 rounded-sm border border-border bg-background text-foreground text-sm outline-none focus:border-accent focus:ring-1 focus:ring-accent/40 max-w-[200px]" />
        </div>
      </Card>

      {/* Iter-194 · Tenant logo upload (white-label) */}
      <LogoUploader />

      <div className="flex items-center gap-2 sticky bottom-4 bg-background/80 backdrop-blur-xl border border-border rounded-sm p-3">
        <Button onClick={save} disabled={busy || !canEdit || !isValidHex}
          className="rounded-sm gap-1.5" data-testid="appearance-save">
          <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save & apply to everyone"}
        </Button>
        <Button variant="outline" onClick={reset}
          className="rounded-sm gap-1.5" data-testid="appearance-reset">
          <RotateCcw className="w-4 h-4" /> Reset to default
        </Button>
        {!canEdit && (
          <div className="text-[11px] text-muted-foreground ml-auto flex items-center gap-1">
            <Palette className="w-3.5 h-3.5" /> Only owners / finance managers can save for the company (you can still preview).
          </div>
        )}
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════════
// Iter-194 · Logo Uploader — sits inside Settings → Appearance
// ═════════════════════════════════════════════════════════════════════════════
function LogoUploader() {
  const { org, user, patchOrgLocal } = useAuth();
  const canEdit = ["business_owner", "super_admin", "finance_manager"].includes(user?.role);
  const [logo, setLogo] = useState(org?.logo_base64 || "");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { setLogo(org?.logo_base64 || ""); }, [org?.logo_base64]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(png|jpe?g|webp|svg\+xml)$/.test(f.type)) {
      toast.error("Use PNG / JPG / WebP / SVG");
      e.target.value = "";
      return;
    }
    compressLogo(f).then(setLogo).catch((err) => toast.error(err.message));
    e.target.value = "";
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/settings/appearance/logo", { logo_base64: logo });
      patchOrgLocal({ logo_base64: logo });
      toast.success("Logo saved — will appear in the sidebar for every user");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await api.post("/settings/appearance/logo", { logo_base64: "" });
      setLogo("");
      patchOrgLocal({ logo_base64: null });
      toast.success("Logo removed — falling back to Zenith 360 default");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-5 rounded-sm" data-testid="logo-uploader">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Company logo</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Replaces the Zenith 360 mark in the top-left of the sidebar for every user of this company. PNG / JPG / WebP / SVG · large images are shrunk automatically · square works best.
          </div>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <div className="w-20 h-20 rounded-sm border border-border bg-muted/40 flex items-center justify-center overflow-hidden">
          {logo ? (
            <img src={logo} alt="Preview" className="max-w-full max-h-full object-contain" data-testid="logo-preview" />
          ) : (
            <span className="text-[10px] font-mono uppercase text-muted-foreground">No logo</span>
          )}
        </div>
        <div className="flex-1 flex flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml"
            onChange={onFile} className="hidden" data-testid="logo-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={!canEdit}
            className="rounded-sm gap-1.5" data-testid="logo-choose">
            <Upload className="w-4 h-4" /> Choose file
          </Button>
          <Button onClick={save} disabled={busy || !canEdit || !logo || logo === org?.logo_base64}
            className="rounded-sm gap-1.5" data-testid="logo-save">
            <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save logo"}
          </Button>
          {org?.logo_base64 && (
            <Button onClick={clear} variant="outline" disabled={busy || !canEdit}
              className="rounded-sm gap-1.5" data-testid="logo-clear">
              <XIcon className="w-4 h-4" /> Remove
            </Button>
          )}
          <div className="text-[11px] font-mono text-muted-foreground ml-auto">
            Stored inline (base64) on the tenant · no external CDN.
          </div>
        </div>
      </div>
    </Card>
  );
}
